import { committedKeys as registryCommittedKeys, DESK_FILTERS, arrayKeys as registryArrayKeys , DATE_DIMS } from './deskFilters.js'
import { LEAD_WATCH_INTENT, LEAD_WATCH_CATEGORY_KIND } from './leadWatchRules.js'
// src/lib/deskSearchState.js — THE definition of committed Desk search state.
// DESK_MODE_REWRITE_PLAN Phase 1, Principle P1: there is exactly ONE definition of
// committed search; every subsystem (URL serialization, isHome, history push/replace,
// refresh restoration, deep links, analytics, saved searches) consumes THIS object.
// No subsystem may reconstruct "what counts as committed" from raw state on its own.
//
// ⚠️ THE LINE THIS MODULE DRAWS (plan §2.2/§2.3 — read before "simplifying"):
//   draft search state      — what the agent is currently expressing: box text,
//                             compiled tokens, token-derived filter patches. Feeds
//                             the live preview and loaded-row narrowing. NEVER
//                             serialized, NEVER consulted by isHome. Lives in
//                             IntakeTab (entrySearch, the live advFilters object).
//   committed search state  — what the agent has actually ASKED for. Written by
//                             Enter, a suggestion click, a recent click, a panel
//                             edit — never by a keystroke. THIS object.
// The original 2026-07-22 bug was a home gate reading state that typing mutates.
// isEmpty() here is safe ONLY because nothing per-keystroke writes committed state.
// Do not add a field without checking what WRITES it (plan §6: "ask what writes it,
// not what reads it").
//
// `browse: true` — the one field with no filter meaning: "the agent explicitly
// entered the feed" (Show-all / Browse CTAs, or any real commit). Without it,
// browse-everything (zero filters) would be indistinguishable from home and the
// Desk would snap to the cockpit the moment the last chip is removed — the exact
// "empty box in browse IS browse-everything" semantic shipped 2026-07-22.

// Filter fields that participate in committed state, in CANONICAL ORDER (Invariant 1:
// one search — one URL; ordering is part of canonicalization). Names match the live
// advFilters shape (ADV_DEFAULTS in IntakeTab).
// ⚠️ THE REGISTRY IS AUTHORITATIVE FROM HERE ON, not merely asserted against.
// Until now deskFilters.js DECLARED each filter and a parity test checked this list agreed
// with it — which caught real bugs but left the registry as documentation: a filter added to
// it alone still worked nowhere. Every registered key is now UNIONED in below, so ONE row in
// deskFilters.js reaches this site by itself.
// The literals stay, in their original order and with their comments: they are the record of
// why each key is here, several of them written the day that key was found missing. Deleting
// them to remove the duplication would delete the reasons, and the union makes the duplication
// harmless — a registered key present twice is the same key.
const COMMITTED_FILTER_KEYS_LITERAL = [
  // B265 (0359/v31): the grammar's Identity third layer. Array of canonical tokens
  // (subtype_map.js), only meaningful under a kind that has a subtype slot — the panel
  // enforces that (every kinds writer prunes orphans), this layer stays kind-agnostic.
  // 0537/0538: streetIds. A street is a stored link now (`property_street`), not a radius,
  // so it is a committed selection exactly like a district code — and like every key here,
  // omitting it would let the chip claim a filter that refresh and share silently drop.
  // Q4 streetNearbyM: committed for the same reason, with one difference worth naming — it
  // is NOT a predicate. It never removes a row; it asks for an extra group. It is here
  // because the committed set is what the snapshot key and the fetch are built from, and a
  // toggle that changed neither would be a checkbox with no effect.
  // 2026-08-27 observability audit: districtOwnCodes was the FOURTH recorded instance of
  // the half-existing-key trap this block documents — picker, chip, URL param, feed rule
  // and transport all existed while this list omitted it, so it filtered correctly on the
  // live request and vanished on refresh/share/restore with the chip still lit.
  // 0688/v48 metroIds: a metro station asked as an IDENTITY (the ad named it), the street
  // rail's twin — committed for the same reason, so refresh and share keep it.
  // 0729/v56 complexIds: a development asked as an IDENTITY (the suggestion click knows the
  // id) — the street rail's twin. Absent from this list = stripped by canonicalFilters on
  // refresh/share while the chip still claims it, the half-existing-key failure above.
  // 📌 THREE keys, TWO branches, ONE trap. Both sessions hit it the same week on different
  // keys; the merge takes the union because dropping either side does not fail loudly — it
  // filters correctly once and then loses the filter on the next refresh.
  'intents', 'kinds', 'subtypes', 'currency', 'districtCodes', 'districtOwnCodes', 'streetIds',
  'complexIds', 'streetNearbyM', 'metroIds',
  'priceMin', 'priceMax', 'roomsSet', 'bedroomsSet',
  'bathroomsMin', 'bathroomsMax',
  // 0757: shipped 2026-08-28 and NOT committed — the filter narrowed the live request and was
  // stripped on refresh and on a shared link, while its pill stayed lit. Found by the new
  // deskFilters registry's parity test within minutes of that registry existing, which is the
  // argument for the registry: every other range min was here and this one was simply forgotten.
  'balconyCountMin', 'balconyCountMax', 'floorMin', 'floorMax',
  'floorsTotalMin', 'floorsTotalMax', 'topFloor',
  'areaMin', 'areaMax',
  // 0345/v29 land area. ⚠️ Absent from this list a filter is silently STRIPPED by
  // canonicalFilters, so refresh/share/restore lose it while its chip still claims it is
  // set — the exact half-existing-key failure this comment block records twice already.
  'landAreaMin', 'landAreaMax',
  // B177 (2026-07-27): client-only exclude filters + payment ceiling. Committed like any
  // other filter (they survive refresh and share via URL); their client-only nature is a
  // FETCH property (LOCAL_ONLY_ADV_KEYS in deskFeed.js), not a state property.
  'restrictions', 'advanceMaxMonths',
  // 0220 step 4 — added 2026-07-27 AFTER shipping without them: a filter absent from this
  // list is silently STRIPPED by canonicalFilters, so refresh/share/restore lost the
  // move-in filter while its chip claimed it was set. If you add a draft filter key, it
  // goes here and in deskUrl.js in the same commit, or it only half-exists.
  'moveInBy', 'moveInHard',
  // 0231: the leads portal filter — missing since 0187 shipped it (the half-existing-key
  // trap this list's own comment warns about): it filtered fine but never survived
  // refresh/share, and a saved search would have silently dropped it.
  'sources',
  // 2026-08-09 — the admin/GM agent filter, the half-existing-key trap's most complete
  // instance yet: it shipped 2026-07-28 wired to exactly TWO sites (the control that sets it
  // and the transport that sends it), deliberately outside advFilters so it would not enter
  // committed state. The cost was not theoretical — `setEntryAgentFilter` had one caller in
  // the whole file, so neither clear button could reset it, and an admin who cleared their
  // search kept a server predicate narrowing the feed, the total, the category tree and the
  // district facet with nothing on screen naming it. Committed here, it survives refresh and
  // share (owner ask), and the chip, the active-filter gate and both clears all fall out of
  // rules that already exist.
  // ⚠️ Role-blind at this layer on purpose — see the note in deskUrl.js PARAM_ORDER.
  'agentIds',
  // B37 (2026-07-29): the amenity filter. Array of `feature.code` strings, AND-ed —
  // server-only (the feed rows carry no feature data), so it goes in SERVER_ONLY_ADV_KEYS
  // too, in this same commit, or every loaded-rows count beside it becomes a lie.
  'featureCodes',
  // featureStates (0756): the four-state feature filter. Committed for the same reason as
  // every key above — absent from this list it filters correctly on the live request and
  // vanishes on refresh/share/restore with the control still showing a state.
  'featureStates',
  // 0433 (v37): the state of the flat — the რემონტი and კარკასი ladders. A SECOND array of
  // `feature.code` strings, deliberately not folded into featureCodes: those AND, these OR
  // (see the facet's header in IntakeTab and the registry entry on the server). Server-only
  // for the same reason featureCodes is — the feed rows carry no property_feature data.
  'conditionCodes',
  // 0249 (2026-07-29). dateField is committed even though it is not a constraint on its
  // own — it is the DIMENSION the range means, so a URL carrying the range without it
  // would restore a different search than the one that was shared ("added since the 20th"
  // becoming "posted since the 20th"). canonicalFilters drops it when no range is set.
  'dateField', 'dateFrom', 'dateTo',
  // B1188. The four combinable date ranges. Committed for the same reason every key here is:
  // omitting it would let a chip claim a filter that a refresh and a shared link silently drop.
  'dateRanges',
  // Leads-only in the UI, but committed unconditionally: the state layer does not know
  // about categories, and a key that exists in only some of them is the half-existing-key
  // trap this list already carries two scars from.
  'claimsLt', 'claimsGt', 'seen',
  // B215 (0262): the exposure-ladder threshold ('' | '0' | '1' | '2'). Same commit adds it
  // to deskUrl.js F2P — the half-existing-key rule above.
  'exposureMax',
  // B213 (0263): the market-spread band ('' | '1' | '2_3' | '4p'). Same commit adds it
  // to deskUrl.js F2P — the half-existing-key rule above.
  'spread',
  // 0294 (v22) — the hunter lenses. Same commit adds every one of these to deskUrl.js F2P
  // and to SERVER_ONLY_ADV_KEYS in deskFeed.js: all six are computed server-side over the
  // full corpus, so a loaded-rows count beside them would be a lie.
  //   sellerType  '' | 'retail' | 'small' | 'pro'   — how many properties the seller holds
  //   ageMinDays / ageMaxDays                        — time on market (listing.posted_at)
  //   ageKinds    array of 'first_posted'|'current_run'|… — WHICH definition of age.
  //               Committed even though it is not a constraint alone, for the same reason
  //               dateField is: it is the dimension the range means, and a URL carrying
  //               the range without it restores a different search.
  //   viewsMin, promoted, footprint (array), ownerEngaged
  // 0431 — committed like moveInHard: a modifier that changes what the activity rows MEAN,
  // so a shared link or a saved search without it restores a different search.
  'keepMine',
  'sellerType', 'ageMinDays', 'ageMaxDays', 'ageKinds',
  // 0303: the spread replacements — band tokens ('0'|'1_2'|'3p' and '1'|'2'|'3p').
  'reposts', 'ownerPlatforms',
  // Advert quality (0702-0704, v55) - committed like every other predicate, so a shared
  // link and a saved search restore the same answer.
  'photos', 'noDescription', 'missingFacts',
  'viewsMin', 'promoted', 'footprint', 'ownerEngaged',
  'q', 'near',
]
// ⚠️ APPENDED, NOT MERGED IN PLACE. This list is in CANONICAL ORDER and that order is part of
// URL canonicalization (Invariant 1: one search — one URL), so a registered key that is
// already in the literal keeps its position; only genuinely new ones land at the end. Today
// that appended set is EMPTY — the parity test proved the two agreed — so this change moves
// no existing key and cannot alter a single URL that exists today.
export const COMMITTED_FILTER_KEYS = buildCommittedKeys()

function buildCommittedKeys() {
  return [...COMMITTED_FILTER_KEYS_LITERAL,
          ...registryCommittedKeys().filter(k => !COMMITTED_FILTER_KEYS_LITERAL.includes(k))]
}

// ⚠️ THE EXPORTED ARRAY IS MATERIALISED AT IMPORT; canonicalFilters MUST NOT BE.
// For production the two are identical — DESK_FILTERS is a static declaration and nothing adds
// a filter at runtime. But an import-time snapshot cannot be exercised: a test that registers a
// probe filter and asks whether it survives a share gets "no", not because the mechanism is
// broken but because the answer was computed before the question. That reads as a failing
// registry and it is neither — which is worse than a red test, because it invites someone to
// "fix" a working mechanism.
// Memoised on the registry's own length, so the hot path pays one integer compare per call and
// nothing else. (Desk canonicalisation runs on every commit and feeds the snapshot key; an
// O(n) rebuild here would be the second avoidable per-call cost found in this file this month.)
let _ckCache = null, _ckLen = -1
function committedKeysLive() {
  if (_ckLen !== DESK_FILTERS.length) { _ckCache = buildCommittedKeys(); _ckLen = DESK_FILTERS.length }
  return _ckCache
}


const ARRAY_KEYS = new Set(['intents', 'kinds', 'subtypes', 'districtCodes', 'districtOwnCodes', 'streetIds', 'complexIds', 'metroIds', 'roomsSet', 'bedroomsSet', 'restrictions', 'sources', 'featureCodes', 'featureStates', 'conditionCodes',
  // 0294: both are multi-select sets, not scalars — omitting them here makes canonicalFilters
  // treat [] as a set value and breaks absent-vs-default serialization (Invariant 1).
  'ageKinds', 'footprint',
  // 2026-08-09: agentIds. Listing a key in COMMITTED_FILTER_KEYS and forgetting it HERE does
  // not half-work, it throws — canonicalFilters falls through to `String(v)`, so the array
  // becomes "id1,id2" and serializeSearch dies on `.join is not a function`. Caught by the
  // round-trip test in the same commit, which is the whole reason that test enumerates every
  // key rather than sampling.
  'agentIds',
  // ⚠️ SEVENTH SITE, found while wiring the other six — the registry's own count of eleven was
  // an undercount. A registered 'set' missing from HERE does not half-work either: it is
  // canonicalised by `String(v)`, so ["a","b"] becomes the literal text "a,b" and the very next
  // `.join` throws. Unioned so a registered set cannot arrive without its array semantics.
  ...registryArrayKeys()])

// Live for the same reason committedKeysLive is, and it earned it the same way: with the
// committed list live but this Set still frozen at import, a registry-only set reached
// canonicalFilters, fell through to `String(v)` and died on `.join` one line later — which is
// precisely the failure the comment above predicts, arriving by a route it did not anticipate.
let _akCache = null, _akLen = -1
function arrayKeysLive() {
  if (_akLen !== DESK_FILTERS.length) { _akCache = new Set([...ARRAY_KEYS, ...registryArrayKeys()]); _akLen = DESK_FILTERS.length }
  return _akCache
}
// Defaults that mean "not set" — omitted from canonical form (Invariant 1:
// absent-vs-default must serialize identically). currency is special: it is a
// display preference that always has a value; it only counts as a constraint
// when a price bound exists (same rule the fetch layer already applies).
const EMPTYISH = v => v == null || v === '' || v === false
  || (Array.isArray(v) && v.length === 0)

// ── STAGE: which inventory the sentence is spoken over (SEARCH_GRAMMAR §3b, 2026-08-02) ──
// Leads is NOT an Object — it is a stage of Property. `property_lead` is keyed on
// property_id and adds only workflow (status/promoted_at/dead_reason), and the server has
// always modelled it as one three-valued `scope` on the SAME search. So the stage sits
// BESIDE the grammar, never inside it: pick the inventory, then the identical sentence
// runs underneath it. Order is the owner's: leads first, and leads is the default.
export const STAGES = ['leads', 'mine', 'all']
// The LANDING default — where a fresh session with no URL stands. Owner: Leads first.
export const DEFAULT_STAGE = 'leads'
// ⚠️ UNSTATED ≠ DEFAULT. `null` means "this URL predates the stage axis", and it must NOT
// become the landing default: an old `type=Property&intent=sale` link would then silently run
// over the LEADS inventory — a different search wearing the same URL. Legacy links resolve to
// the OLD role-derived scope at the component boundary; only a fresh landing gets DEFAULT_STAGE.
export const normStage = s => (STAGES.includes(s) ? s : null)

// ── THE `restrictionsFrom` MARKER (design handoff desk-search, turn 5 / 6b) ────────────
// WHICH restriction lenses came from a client, and which client. That is all it is: a
// provenance record riding beside `filters.restrictions`, never a second copy of them and
// never a filter — it narrows nothing and cannot make a search non-empty on its own.
//
// It exists because 6b's presentation has to draw a distinction the filter state cannot
// express: a client with a cat and no stated nationality produces `pol_cat:hide` and
// SILENCE on nationality, and silence there means UNKNOWN, not "no restriction". Without
// a marker, a derived lens and a hand-set one are the same string, so the panel can only
// say "these filters are on" — it cannot say "these came from Nino, and these axes nobody
// asked her about", which is exactly the sentence 6b is.
//
// ⚠️ DERIVED AND MANUAL ARE ONE STATE, NOT TWO MODES (the brief's own words). The marker
// therefore holds no answers of its own — every entry it names must still be present,
// VERBATIM, in `filters.restrictions`. The moment the agent edits or removes one, that
// entry drops out of the marker and the lens simply becomes theirs. Storing the derived
// answers separately would create a second copy that can disagree with the first, which
// is the failure this codebase has logged under half a dozen names.
//
// ⚠️ CODES, not axes. The design writes `derivedAxes: axisId[]`, and the axis of a code is
// knowable only through `restriction_code_projection` — which the server ships and
// `whoIsItFor.js` already reads. Putting a code→axis map in this layer would be a SECOND
// definition of a mapping that changes with the vocabulary; the presentation derives axes
// from these codes with the same projection it uses to draw the rows. Same information,
// one definition.
//
// Entries are the full `code:mode` form so a MODE FLIP counts as an override: an agent who
// turns the client's `pol_cat:hide` into `pol_cat:allow` has overruled the client, and a
// marker still claiming that lens would put the client's name on the agent's answer.
export function canonicalRestrictionsFrom(raw, canonFilters) {
  if (!raw || !raw.clientId) return null
  const live = new Set((canonFilters && canonFilters.restrictions) || [])
  const derived = [...new Set((raw.derived || []).map(String))].filter(e => live.has(e)).sort()
  // Nothing of the client's survives → there is nobody to attribute anything to. This is
  // also what "clearing the client keeps the derived answers as manual ones" reduces to:
  // drop the marker, keep the filters.
  if (!derived.length) return null
  return { clientId: String(raw.clientId), derived }
}

export function emptyCommitted() {
  return { browse: false, type: '', stage: null, city: '', filters: {}, restrictionsFrom: null }
}

// Canonicalize a raw filters object into committed form: only known keys, arrays
// sorted + deduped, empty/default values dropped, near reduced to its stable
// identity fields. Pure — never mutates the input.
// The date dimensions, read from the registry rather than restated -- a second list here would
// be one more place to forget a dimension, which is the half-existing-key trap in miniature.
const DATE_DIMS_LIVE = () => DATE_DIMS

export function canonicalFilters(raw) {
  const out = {}
  if (!raw) return out
  for (const k of committedKeysLive()) {
    let v = raw[k]
    if (EMPTYISH(v)) continue
    if (arrayKeysLive().has(k)) {
      v = [...new Set(v.map(String))].sort()
      if (!v.length) continue
    } else if (k === 'near') {
      if (v.lat == null || v.lng == null) continue
      v = { id: v.id ?? null, lat: Number(v.lat), lng: Number(v.lng), radiusM: Number(v.radiusM) || 1000 }
    } else if (k === 'topFloor' || k === 'moveInHard' || k === 'keepMine'
            || k === 'noDescription' || k === 'missingFacts'
            || DESK_FILTERS.some(f => f.key === k && f.kind === 'flag')) {   // registry-first: every flag row (firstFloor, the ✕ halves) canonicalises to true
      v = true
    } else if (k === 'dateRanges') {
      // B1188. An OBJECT, so it must skip the String(v) fall-through below -- which is exactly
      // where it landed first, committing the literal text [object Object] and producing one
      // filter that nothing matches with no error anywhere. Normalised the same way `near` is:
      // rebuilt field by field, so an unknown key or a stray value cannot ride along into
      // committed state, and dimensions come out in a stable order so two equal searches
      // compare equal.
      const norm = {}
      for (const dim of DATE_DIMS_LIVE()) {
        const r = v && v[dim]
        if (!r || (!r.from && !r.to)) continue
        norm[dim] = { from: String(r.from || ''), to: String(r.to || '') }
      }
      if (!Object.keys(norm).length) continue
      v = norm
    } else if (k === 'q') {
      v = String(v).trim()
      if (!v) continue
    } else if (k !== 'currency') {
      v = String(v)
    }
    out[k] = v
  }
  // F-092 (owner, 2026-09-09): an INVERTED range is not a search. Min 1,000 / max 500 was accepted as
  // typed and shown as 1,331 "possible matches" — every row failing an impossible predicate landed on
  // the shelf. The owner's ruling was to fix it; the fix taken is to read the two numbers as the range
  // the agent meant (the boxes were filled the wrong way round) and SWAP them here, at the one canonical
  // layer — so the URL, the chips, the wire and the counts all carry the sane range and no consumer can
  // see the inverted one. Registry-driven: every 'range' row (price, area, landArea, floor, floorsTotal,
  // bathrooms, balconyCount), so a range added tomorrow is covered by construction. Equal ends are a
  // legitimate exact match and are left alone.
  for (const f of DESK_FILTERS) {
    if (f.kind !== 'range') continue
    const lo = f.key + 'Min', hi = f.key + 'Max'
    if (out[lo] == null || out[hi] == null) continue
    const a = Number(out[lo]), b = Number(out[hi])
    if (Number.isFinite(a) && Number.isFinite(b) && a > b) { out[lo] = String(b); out[hi] = String(a) }
  }
  // currency counts only alongside a money bound — total price OR price per m² (else it is a preference, not a filter).
  // ⚠️ Both ranges, always (filters audit 2026-10-27 F03): ppm was left out, so "GEL ≤ 1,200/m²" was written as a bare
  // ppmMax and came back as a USD question on restore.
  if (out.currency != null && out.priceMin == null && out.priceMax == null && out.ppmMin == null && out.ppmMax == null) delete out.currency
  // a hard-need flag with no date is meaningless — never serialize one without the other
  if (out.moveInHard && out.moveInBy == null) delete out.moveInHard
  // 0431 — same rule: "…except mine" qualifies the team-activity rows, so without them it
  // selects nothing. Left in, it would mark an EMPTY search as non-empty and open the feed
  // on a modifier alone — the client-side twin of the server's `notAPredicate`.
  if (out.keepMine && out.footprint == null && out.exposureMax == null) delete out.keepMine
  // Age kinds answer *which clock* an age range uses; they are not a predicate on their
  // own.  Keep the committed state aligned with the request builder, which deliberately
  // sends `age_kinds` only beside an age bound.  Otherwise a picked clock opens a
  // shareable search whose URL/chip says it is filtering while the server receives no
  // constraint at all.
  if (out.ageKinds != null && out.ageMinDays == null && out.ageMaxDays == null) delete out.ageKinds
  // 2026-10-22: the ✕ half of the same clock pick, same rule. Kept alone it marked a search
  // the request builder sent without any age constraint, and the age chip could not clear it.
  if (out.ageKindsNot != null && out.ageMinDays == null && out.ageMaxDays == null) delete out.ageKindsNot
  // 2026-08-16 — the SAME rule again, and this one reached production: a nearby-street
  // RADIUS selects nothing without a street to measure from, and a radius of 0 is the
  // control's "off" position. `ADV_DEFAULTS.streetNearbyM` is `0`, and EMPTYISH above
  // covers null/''/false/[] but NOT the number 0 — so a Desk on which nobody had typed
  // anything canonicalized to `{streetNearbyM:"0"}`, which made `browse` true, which
  // opened the FEED instead of the home view for every user, on every load, and wrote a
  // permanent `#datadesk?streets_near=0` into the URL. The home view (leads / clients /
  // meetings) was simply unreachable. Shipped 2026-08-14 by d0b8d97 and reported as
  // "main view is gone".
  // ⚠️ The guard is here rather than in EMPTYISH on purpose: making 0 emptyish globally
  // would silently reinterpret every other numeric key, where 0 can be a real answer.
  // `core.js` already applies the streetIds half of this rule when calling the API — the
  // canonical form now agrees with it, instead of disagreeing one layer up.
  if (out.streetNearbyM != null && (Number(out.streetNearbyM) === 0 || out.streetIds == null)) delete out.streetNearbyM
  // …same rule for the date dimension: it selects nothing by itself (0249). Dropping it
  // keeps Invariant 1 — "added" with no range and "posted" with no range are ONE search,
  // and must therefore be one URL.
  if (out.dateField != null && out.dateFrom == null && out.dateTo == null) delete out.dateField
  // B1188: {} is not a filter. Without this an untouched search commits an empty object,
  // activeIn's own isSet says false but the committed SHAPE still differs from the default,
  // and every consumer that compares committed state sees a change that is not one.
  if (out.dateRanges != null && !Object.keys(out.dateRanges).length) delete out.dateRanges
  return out
}

// Build a canonical committed state. type = entry-type category ('' = all);
// city = city filter key ('' / 'all' = none).
export function makeCommitted({ browse = false, type = '', stage = null, city = '', filters = null, restrictionsFrom = null, pool = null } = {}) {
  const f = canonicalFilters(filters)
  // F-087 (2026-09-07): the POOL is a pile axis like stage — 'archived' or null (= active, the landing default, never
  // written). It answers "which pile" for BOTH book scopes (Our Work's status pill) and leads (the archive flag). A
  // pool alone is not a search (browse stays false), exactly as a bare stage is not; it rides the URL so a refresh or a
  // shared link lands on the archive the agent was looking at (measured: Archive 456 → refresh → 3,990 active).
  const pl = pool === 'archived' ? 'archived' : null
  const st = normStage(stage)   // null when unstated or unrecognised — see normStage
  return {
    // A STATED stage is an act ("show me Mine") and opens the feed rather than home. An
    // unstated one is not a search by itself, so it never marks one.
    // ⚠️ `restrictionsFrom` is deliberately NOT in this expression. It can only survive
    // canonicalization alongside the restrictions it names, so it can never be the only
    // thing set — and if it could, "who a filter came from" would be opening the feed
    // without a filter behind it.
    browse: !!browse || !!type || st != null || !!Object.keys(f).length,
    type: type || '',
    stage: st,
    pool: pl,   // F-087
    city: !city || city === 'all' ? '' : String(city),
    filters: f,
    // Canonicalized AGAINST the canonical filters, so pruning is one rule applied in one
    // place: whatever the caller passes, what comes out names only lenses that are really
    // in this search.
    restrictionsFrom: canonicalRestrictionsFrom(restrictionsFrom, f),
  }
}

// THE isHome predicate (plan §2.1): home is what the Desk renders when there is
// no committed search. Reads ONLY committed state — see the header warning.
export function isEmptyCommitted(c) {
  return !c || (!c.browse && !c.type && !c.city && Object.keys(c.filters || {}).length === 0)
}

// Structural equality over canonical forms — the push-vs-replace decision and
// "did the URL actually change" both need it. Canonical form makes JSON stable.
export function sameCommitted(a, b) {
  return JSON.stringify(makeCommitted(a || {})) === JSON.stringify(makeCommitted(b || {}))
}

// Reverse map (URL restore, stage 2c): committed.filters (canonical form) → the LIVE
// advFilters shape. `defaults` is an ADV_DEFAULTS-like template that supplies the field
// TYPES the panel expects: '' for unset scalars, [] arrays, false topFloor, null near.
// roomsSet/bedroomsSet come back as NUMBERS (canonical form stringifies them for sorting);
// other scalars come back as strings. currency: canonical form carries it only alongside
// a price bound — otherwise the template's value (the global top-bar toggle) is kept, so
// restoring a URL never silently flips the agent's display currency.
export function committedToAdvFilters(committed, defaults) {
  const c = makeCommitted(committed || {})
  const out = { ...(defaults || {}) }
  for (const [k, v] of Object.entries(c.filters)) {
    if (k === 'roomsSet' || k === 'bedroomsSet') out[k] = v.map(Number).filter(Number.isFinite)
    else if (ARRAY_KEYS.has(k)) out[k] = [...v]
    else if (k === 'near') out[k] = { ...v }
    // 2026-10-22: every registry 'flag' row comes back as true, the canonicalFilters rule above.
    // The hand list missed firstFloor, moveInHard and the ✕ halves (topFloorNot, firstFloorNot):
    // they came back as the string "true", so the control and its ✕ chip read unset while the
    // request still sent the flag -- a filter on the wire nobody could see or clear.
    else if (k === 'topFloor' || k === 'keepMine'
          || k === 'noDescription' || k === 'missingFacts'
          || DESK_FILTERS.some(f => f.key === k && f.kind === 'flag')) out[k] = true
    // …and dateRanges is an OBJECT (B1188): String(v) restored a shared link as "[object Object]".
    else if (k === 'dateRanges') out[k] = Object.fromEntries(Object.entries(v).map(([d, r]) => [d, { ...r }]))
    else out[k] = String(v)
  }
  return out
}

// §1.3 — history is about INTENT: pushState when the user begins a NEW search
// context, replaceState while REFINING the current one. Heuristic (documented,
// revisable): a new context = home→search, or the committed TEXT changed, or the
// TYPE category changed. Everything else (facet tweaks, city, near radius…) is
// refinement of the current context.
export function isNewContext(prev, next) {
  if (isEmptyCommitted(prev) && !isEmptyCommitted(next)) return true
  const p = makeCommitted(prev || {}), n = makeCommitted(next || {})
  if ((p.filters.q || '') !== (n.filters.q || '')) return true
  if (p.type !== n.type) return true
  return false
}

// ── B113: a client's requirement, expressed as a Desk search over LEADS ──────────────
// "No good fit? Search leads for this requirement." Matching stays clean — it only ever
// considers the agency's own supply — while the ~18k crawler leads become reachable at
// exactly the moment supply is thin, which is the moment an agent would otherwise give
// up or go browse a portal by hand.
//
// The output is an ordinary committed search, so it travels the EXISTING apply path
// (serializeSearch → location.hash → the restore that already runs on load). No new
// navigation code, same rule saved searches follow.
//
// ⚠️ Maps ONLY what the requirement actually states. Two consequences worth keeping:
//   · `kinds` comes from requirement.property_category, and ONLY when the category names a
//     kind (leadWatchRules.js LEAD_WATCH_CATEGORY_KIND) — "other"/unknown sets nothing, because
//     guessing "apartment" would narrow the hatch on an assumption the client never made.
//     (Until 2026-09-21 no category was passed at all: a flat client's hatch and Telegram
//     alert both searched land and commercial too.) The demand intent is translated to the
//     supply vocabulary the same way: a `buy` client searches `sale` leads.
//   · both district tiers collapse into one OR-set. In matching, `must` is a hard gate
//     and `codes` are desirable; a browse filter has no such distinction, and dropping
//     the soft tier would hide leads the client would happily look at.
// Rooms/bedrooms become checkbox SETS because that is what the panel and the RPC speak;
// an open-ended max folds into the 5+ box, the same envelope facetSetEnvelope uses.
// `clientId` — the requirement this search was derived FROM. Optional, and the hatch works
// exactly as before without it (the marker is simply absent, which is the honest reading:
// nothing knows where these lenses came from). See canonicalRestrictionsFrom.
export function requirementToCommitted(criteria, intent, clientId = null, propertyCategory = null) {
  const c = criteria || {}
  const num = v => (v == null || v === '' ? null : Number(v))
  const filters = {}
  if (LEAD_WATCH_INTENT[intent]) filters.intents = [LEAD_WATCH_INTENT[intent]]
  if (LEAD_WATCH_CATEGORY_KIND[propertyCategory]) filters.kinds = [LEAD_WATCH_CATEGORY_KIND[propertyCategory]]

  const districts = [...new Set([...(c.district_must_codes || []), ...(c.district_codes || [])])]
  if (districts.length) filters.districtCodes = districts
  // A client's excluded districts are the ✕ half (2026-10-22: they were dropped, so the hatch showed them).
  const notDistricts = [...new Set(c.excluded_district_codes || [])]
  if (notDistricts.length) filters.districtCodesNot = notDistricts

  const setFrom = (lo, hi) => {
    const a = num(lo), b = num(hi)
    if (a == null && b == null) return null
    // (2026-10-22: a max with no min starts at 1 — {rooms_max: 3} was the 3 box alone, not 1–3)
    const from = Math.max(1, Math.min(5, Math.round(a ?? 1)))
    const to = b == null ? 5 : Math.max(from, Math.min(5, Math.round(b)))
    const out = []
    for (let n = from; n <= to; n++) out.push(n)
    return out.length ? out : null
  }
  const rooms = setFrom(c.rooms_min, c.rooms_max)
  if (rooms) filters.roomsSet = rooms
  const beds = setFrom(c.bedrooms_min, c.bedrooms_max)
  if (beds) filters.bedroomsSet = beds

  if (num(c.price_min) != null) filters.priceMin = String(num(c.price_min))
  if (num(c.price_max) != null) filters.priceMax = String(num(c.price_max))
  // currency only means something beside a bound — sending it alone would route an
  // otherwise-unfiltered search through the RPC for nothing (the core.js rule).
  if ((filters.priceMin || filters.priceMax) && c.currency) filters.currency = c.currency

  if (num(c.area_sqm_min) != null) filters.areaMin = String(num(c.area_sqm_min))
  if (num(c.area_sqm_max) != null) filters.areaMax = String(num(c.area_sqm_max))
  if (num(c.land_sqm_min) != null) filters.landAreaMin = String(num(c.land_sqm_min))
  if (num(c.land_sqm_max) != null) filters.landAreaMax = String(num(c.land_sqm_max))
  if (num(c.floor_min) != null) filters.floorMin = String(num(c.floor_min))
  if (num(c.floor_max) != null) filters.floorMax = String(num(c.floor_max))
  // ⚠️ The client's HARD requirements ride the hatch too (Desk search audit 2026-10-29: 69 of 311 open clients have
  // must-haves, 44 conditions, 16 a floor exclusion — all dropped, so "N leads" counted flats the client ruled out).
  // Safe to send: features and the condition ladder are three-verdict server filters, so a lead that never recorded
  // the feature lands on the possible-matches shelf instead of being excluded (searchWire.js, features / condition).
  // Not carried: search_polygon / near_points — the hatch has no drawn-area or multi-point filter to put them in.
  const codes = v => [...new Set((Array.isArray(v) ? v : []).filter(Boolean))]
  if (codes(c.must_have_codes).length) filters.featureCodes = codes(c.must_have_codes)
  if (codes(c.must_not_have_codes).length) filters.featureStates = codes(c.must_not_have_codes).map(code => `${code}:no`)
  if (codes(c.condition_codes).length) filters.conditionCodes = codes(c.condition_codes)
  if (codes(c.condition_exclude_codes).length) filters.conditionExcludeCodes = codes(c.condition_exclude_codes)
  if (c.floor_not_first === true) filters.firstFloorNot = true
  if (c.floor_not_last === true) filters.topFloorNot = true

  // ── The HOUSEHOLD rides the hatch (design handoff desk-search 6b, 2026-08-05) ──────────
  // criteria.household_codes IS the tenant_terms vocabulary the restriction filter speaks,
  // and `hide` is exactly its question — "find places that take this client" (recorded
  // refusals drop out; the unrecorded pass, B206). Until this line the hatch dropped the
  // household entirely: a client WITH A CAT opened the leads feed carrying price and rooms
  // but no pet lens, so no-pets listings read as fine — the derivation gap 6b names.
  // A retired code an old client still carries is harmless here: nothing records evidence
  // against it any more, so its lens passes everything rather than filtering wrong.
  if (Array.isArray(c.household_codes) && c.household_codes.length)
    filters.restrictions = c.household_codes.map(code => `${code}:hide`)

  // The lenses this hatch just derived are ALL of them and only them — every entry above
  // came from `household_codes`, nothing here is hand-set. That is what makes this the one
  // honest place to stamp the marker: further down the panel, "which of these did the
  // client answer" is no longer recoverable, which is precisely why the marker exists.
  // §3b: the hatch opens the LEADS inventory — that is a stage, not an object type.
  return makeCommitted({
    browse: true, stage: 'leads', filters,
    restrictionsFrom: clientId && filters.restrictions ? { clientId, derived: filters.restrictions } : null,
  })
}

// 2026-10-22: the agent facet's counts (getAgentCounts, 0232) take only the period — no other
// filter reaches them. Beside "Vake, 2 rooms" they printed each agent's WHOLE-book number, so the
// click delivered far fewer than the facet promised. True when nothing but the agent facet itself
// narrows the committed search (type, city and every other filter absent): only then do the counts
// describe what a click returns. The caller hides them otherwise.
const AGENT_FACET_KEYS = new Set(['agentIds', 'agentIdsNot'])
export function agentCountsDescribeSearch(committed) {
  const c = makeCommitted(committed || {})
  if (c.type || c.city) return false
  return Object.keys(c.filters).every(k => AGENT_FACET_KEYS.has(k))
}
